import { type ColDef, type TooltipCallbackParams } from "ag-grid-community"
import { isEqual, max, mean, min, sum, uniqBy } from "lodash"

import { type StorageLocation } from "~/server/api/routers/storageLocations"
import { storageLocationColId } from "~/utils/storageLocationColumns"
import {
  formatNumber,
  formatNumberWithSuffix,
  formatUniqueValues,
  uniqueValueFormatter,
} from "~/utils/formatting"
import { groupBy, uniq } from "~/utils/tsUtils"
import { type IVariantSummary } from "~/hooks/useVariantTableData"
import { DateFormatType, formatDate } from "~/utils/datetime"
import { isValid } from "date-fns"
import {
  extraDataFieldNameFromColumnId,
  getExtraDataDisplayName,
  getExtraDataAggregation,
  getExtraDataType,
  parseExtraDataValue,
  ExtraDataTable,
} from "~/utils/dynamicTableUtils"

export type DynamicColType = "number" | "string" | "date"
export type DynamicColAggregation = "first" | "sum" | "avg" | "max" | "min" | "unique"

/**
 * Creates dynamic columns for any row carrying SKU extraData, one per "<field>(extradata)" id
 */
export function skuExtraDataColumns<T extends { extraData?: unknown }>(
  extraFields: string[],
  userSetDisplayNameMap?: Map<string, string>,
  columnTypeMap?: Map<string, DynamicColType>,
  extraDataAggregationMap?: Map<string, DynamicColAggregation>,
): ColDef<T>[] {
  return extraFields.map((key) => {
    const keyName = extraDataFieldNameFromColumnId(key)
    const colType = getExtraDataType(keyName, ExtraDataTable.SKU, columnTypeMap)
    const baseColConfig: ColDef<T> = {
      headerName: getExtraDataDisplayName(keyName, ExtraDataTable.SKU, userSetDisplayNameMap),
      headerTooltip: "Dynamic field generated from external data on the SKUs",
      colId: key,
      valueGetter: (params) => {
        const extraData = params.data?.extraData as Record<string, string | number> | undefined
        return parseExtraDataValue(extraData?.[keyName], colType)
      },
    }
    const typeColConfig = configDynamicCol<T>(
      colType,
      getExtraDataAggregation(keyName, ExtraDataTable.SKU, extraDataAggregationMap),
    )

    const tooltipConfig: ColDef<T> | undefined =
      colType === "string"
        ? {
            tooltipComponentParams: {
              title: getExtraDataDisplayName(keyName, ExtraDataTable.SKU, userSetDisplayNameMap),
              tooltipType: "UniqueList",
            },
            tooltip: (params) => {
              return params.value
            },
          }
        : undefined
    return { ...baseColConfig, ...typeColConfig, ...tooltipConfig }
  })
}

export function dynamicTableColumnsV2(
  extraFields: string[],
  collectionExtraFields: string[],
  userSetDisplayNameMap?: Map<string, string>,
  columnTypeMap?: Map<string, DynamicColType>,
  extraDataAggregationMap?: Map<string, DynamicColAggregation>,
  collectionIds?: number[],
): ColDef<IVariantSummary>[] {
  const columns: ColDef<IVariantSummary>[] = skuExtraDataColumns<IVariantSummary>(
    extraFields,
    userSetDisplayNameMap,
    columnTypeMap,
    extraDataAggregationMap,
  )

  collectionExtraFields.forEach((key) => {
    const keyName = key.replace("(collection-extradata)", "")
    const colType = getExtraDataType(keyName, ExtraDataTable.COLLECTION_ON_SKU, columnTypeMap)
    const baseColConfig: ColDef<IVariantSummary> = {
      headerName: getExtraDataDisplayName(
        keyName,
        ExtraDataTable.COLLECTION_ON_SKU,
        userSetDisplayNameMap,
      ),
      headerTooltip: "Dynamic field generated from external data on the SKUs",
      colId: key,
      equals: (v1, v2) => isEqual(v1, v2),
      valueGetter: (params) => {
        let collections = (params.data as IVariantSummary)?.collections
        if (!collections || collections.length === 0) {
          return null
        }

        if (collectionIds && collectionIds.length > 0) {
          collections = collections.filter((x) => collectionIds.includes(Number(x.id)))
          if (collections.length === 0) return null
        }

        const extraData = collections.map((x) => ({
          value: x.extraData?.[keyName],
          collectionName: x.name,
        }))

        const parsedData = extraData
          .filter((x) => x.value !== undefined)
          .map((x) => ({
            value: (x.value = parseExtraDataValue(x.value, colType)),
            collectionName: x.collectionName,
          }))
        if (typeof parsedData[0]?.value === "number") {
          const data = parsedData.filter((x) => typeof x.value === "number")
          if (data.length === 0) return null
          // The data output is on variant level, but we need to aggregate it when the variant is in multiple collections.
          const aggregation = getExtraDataAggregation(
            keyName,
            ExtraDataTable.COLLECTION_ON_SKU,
            extraDataAggregationMap,
          )
          const values = data.map((x) => x.value as number)

          switch (aggregation) {
            case "first":
              return uniq(values).sort()
            case "sum":
              return sum(values)
            case "avg":
              return mean(values)
            case "min":
              return min(values)
            case "max":
              return max(values)
            default:
              return uniq(values).sort()
          }
        } else {
          return uniq(parsedData.map((x) => x.value).sort())
        }
      },
    }

    const aggregation = getExtraDataAggregation(
      keyName,
      ExtraDataTable.COLLECTION_ON_SKU,
      extraDataAggregationMap,
    )
    const typeColConfig = configDynamicCol<IVariantSummary>(
      colType,
      aggregation === "first" && colType === "number" ? "unique" : aggregation,
    )
    const tooltipConfig: ColDef<IVariantSummary> | undefined = {
      tooltipComponentParams: {
        title: getExtraDataDisplayName(
          keyName,
          ExtraDataTable.COLLECTION_ON_SKU,
          userSetDisplayNameMap,
        ),
        tooltipType: "UniqueList",
      },
      tooltip: (params: TooltipCallbackParams<IVariantSummary>) => {
        let collections = params.node?.group
          ? (params.node.allLeafChildren?.flatMap((c) => c.data?.collections ?? []) ?? [])
          : (params.data?.collections ?? [])

        if (collectionIds && collectionIds.length > 0) {
          collections = collections.filter((x) => collectionIds.includes(Number(x.id)))
        }

        let values = collections.map((x) => ({
          value:
            x.extraData?.[keyName] == undefined
              ? "No value"
              : parseExtraDataValue(x.extraData?.[keyName], colType),
          collectionName: x.name,
        }))

        if (
          getExtraDataType(keyName, ExtraDataTable.COLLECTION_ON_SKU, columnTypeMap) !== "number"
        ) {
          values = uniqBy(values, (x) => `${x.value}-${x.collectionName}`)
        }

        // Group by collectionName
        const grouped = groupBy(values, (x) => x.value)
        if (Object.keys(grouped).length === 1) {
          return values[0]?.value ?? "-"
        }

        // Group by value, then list all collectionNames for each value
        return Object.entries(grouped).map(([value, items]) => {
          const itemNames = uniq(items.map((item) => item.collectionName.trim()))
          return `${value} (${itemNames.length > 5 ? itemNames.length + " collections" : itemNames.join(", ")})`
        })
      },
    }

    const colDef = { ...baseColConfig, ...typeColConfig, ...tooltipConfig }
    columns.push(colDef)
  })

  return columns
}

// TODO: How can we make it good with extraColumns?
export function storageLocationColumns(
  storageLocations: StorageLocation[],
): ColDef<IVariantSummary>[] {
  const columns: ColDef<IVariantSummary>[] = []

  storageLocations.forEach((storageLocation) => {
    const id = storageLocationColId(storageLocation.id)
    columns.push({
      headerName: `Stock level: ${storageLocation.name}`,
      colId: id,
      valueGetter: (params) => {
        if (!params.data?.storageLocationData) return undefined
        return sum(
          params.data.storageLocationData
            .filter((x) => x?.locationId == storageLocation.id)
            .map((x) => x?.stockLevel ?? 0),
        )
      },
      aggFunc: "sum",
      valueFormatter: (params) =>
        params.value !== undefined ? formatNumberWithSuffix(params.value, 1) : "-",
      filter: "agNumberColumnFilter",
    })
  })

  return columns
}

const configDynamicCol = <T,>(
  type: DynamicColType,
  aggregation: DynamicColAggregation,
): ColDef<T> => {
  switch (type) {
    case "number":
      return {
        enableValue: true, // Allows user to configure aggFunc through UI
        aggFunc: aggregation,
        valueFormatter: (params) => {
          if (params.value == undefined || params.value.length === 0) {
            return "-"
          }
          if (Array.isArray(params.value)) {
            return formatUniqueValues(params.value)
          }
          return formatNumber(params.value, 1)
        },
      }
    case "string":
      return {
        aggFunc: "unique",
        valueFormatter: (params) => uniqueValueFormatter(params, "values", true),
      }
    case "date": {
      const dateAggFunc =
        aggregation === "min"
          ? (params: any) => min(params.values.filter(Boolean))
          : aggregation === "max"
            ? (params: any) => max(params.values.filter(Boolean))
            : aggregation
      return {
        aggFunc: dateAggFunc,
        valueFormatter: (params) => {
          if (!params.value) return "-"
          const raw = Array.isArray(params.value) ? params.value[0] : params.value
          const d = new Date(raw)
          return isValid(d) ? formatDate(d, DateFormatType.Default) : String(raw)
        },
      }
    }
    default:
      throw new Error(`Unable to configure Dynamic Column with type: ${type}`)
  }
}

/**
 * Creates dynamic columns for IncomingOrderRow based on extraData fields
 */
export function incomingOrderDynamicColumns<
  T extends { extraData?: Record<string, unknown> | null },
>(
  extraFields: string[],
  userSetDisplayNameMap?: Map<string, string>,
  columnTypeMap?: Map<string, DynamicColType>,
  extraDataAggregationMap?: Map<string, DynamicColAggregation>,
): ColDef<T>[] {
  const columns: ColDef<T>[] = []

  extraFields.forEach((key) => {
    const keyName = key.replace("(incoming-order-extradata)", "")
    const colType = getExtraDataType(keyName, ExtraDataTable.INCOMING_ORDER_ITEM, columnTypeMap)
    const aggregation = getExtraDataAggregation(
      keyName,
      ExtraDataTable.INCOMING_ORDER_ITEM,
      extraDataAggregationMap,
    )

    const baseColConfig: ColDef<T> = {
      headerName: getExtraDataDisplayName(
        keyName,
        ExtraDataTable.INCOMING_ORDER_ITEM,
        userSetDisplayNameMap,
      ),
      headerTooltip: "Dynamic field from incoming order extra data",
      colId: key,
      valueGetter: (params) => {
        const extraData = params.data?.extraData as
          | Record<string, string | number>
          | null
          | undefined
        const value = extraData?.[keyName]
        if (value === null || value === undefined) return null
        return parseExtraDataValue(value, colType)
      },
    }

    const typeColConfig: ColDef<T> =
      colType === "number"
        ? {
            enableValue: true,
            aggFunc: aggregation,
            valueFormatter: (params) => formatNumber(params.value, 1),
          }
        : {
            aggFunc: "unique",
            valueFormatter: (params) => uniqueValueFormatter(params, "values", true),
          }

    const tooltipConfig: ColDef<T> | undefined =
      colType === "string"
        ? {
            tooltipComponentParams: {
              title: getExtraDataDisplayName(
                keyName,
                ExtraDataTable.INCOMING_ORDER_ITEM,
                userSetDisplayNameMap,
              ),
              tooltipType: "UniqueList",
            },
            tooltip: (params) => params.value,
          }
        : undefined

    const colDef = { ...baseColConfig, ...typeColConfig, ...tooltipConfig }
    columns.push(colDef)
  })

  return columns
}
